import { ComponentProps } from 'react';
import { DeliveryImplantInspection, DeliveryStatus } from 'api/models/Deliveries';
import { Implant } from 'api/models/Implants';
import { OrderStatus } from 'api/models/Orders';
import { Set } from 'api/models/Sets';
import theme from 'App.theme';
import Button from 'components/Button';

export type Status = DeliveryStatus | OrderStatus;
type ThemeStatus = keyof typeof theme.colors.statuses;

// Some delivery/order statuses are grouped together in the theme, e.g. DISPATCHED and OUTBOUND should both render in purple
// The SET_BUTTON_THEME_STATUS_MAPPING uses types to ensure that we don't miss any delivery or order statuses
// E.g. If you add a new delivery/order status, this will throw an error and you should add it to the mapping
// NOTE: This mapping is different to the one in StatusChip.tsx
export const SET_BUTTON_THEME_STATUS_MAPPING: Record<Status, ThemeStatus> = {
  // Delivery Status
  ORDERED: 'ORDERED',
  PARTIALLY_DISPATCHED: 'PARTIALLY_DISPATCHED',
  DISPATCHED: 'DISPATCHED',
  OUTBOUND: 'DISPATCHED',
  DELIVERED: 'DELIVERED',
  PARTIALLY_DELIVERED: 'PARTIALLY_DELIVERED',
  RECEIVED: 'RECEIVED',
  OVERDUE_FOR_CHECK_IN: 'RECEIVED',
  PARTIALLY_CHECKED_IN: 'RECEIVED',
  CHECKED_IN: 'CHECKED_IN',
  OVERDUE_FOR_CHECK_OUT: 'CHECKED_IN',
  PARTIALLY_CHECKED_OUT: 'CHECKED_IN',
  CHECKED_OUT: 'CHECKED_OUT',
  DELETED: 'DELETED',
  CANCELED: 'CANCELED',
  NOT_DELIVERED: 'NOT_DELIVERED',
  // Order Status
  Default: 'ORDERED',
  REQUESTED: 'ORDERED', // Won't have sets but included for brevity
  BOOKED: 'ORDERED', // Ditto
  PARTIALLY_RECEIVED: 'DISPATCHED',
  RECEIVE: 'RECEIVED',
  INSPECTION: 'RECEIVED', // Deprecated but included for brevity
  CHECK_IN: 'CHECKED_IN',
  CHECK_OUT: 'CHECKED_OUT',
  PARTIALLY_RETURNED: 'RECEIVED',
  RETURNED: 'RETURNED',
  INBOUND: 'CHECKED_OUT',
};

// Type guard to check if the inspections object is of type Set['inspections']
const isSetInspections = (inspections: any): inspections is Set['inspections'] => {
  return inspections && (inspections.CHECK_OUT || inspections.CHECK_IN);
};

// Type guard to check if the inspections object is of type Implant['inspection']
const isImplantInspections = (inspections: any): inspections is Implant['inspection'] => {
  return inspections && (inspections.CHECK_OUT || inspections.CHECK_IN);
};

const getThemeStatus = ({
  status,
  inspections,
  implantInspection,
}: {
  status: Status;
  inspections: Set['inspections'] | Implant['inspection'];
  implantInspection?: DeliveryImplantInspection;
}): ThemeStatus => {
  // We need to override the status for these statuses
  if (status == 'CANCELED') return 'CANCELED';
  if (status == 'DELETED') return 'DELETED';
  if (status == 'NOT_DELIVERED') return 'NOT_DELIVERED';
  if (status == 'CHECK_OUT' || status == 'CHECKED_OUT') return 'CHECKED_OUT';
  if (status == 'RETURNED') return 'RETURNED';
  if (status == 'PARTIALLY_RETURNED') return 'RECEIVED';

  // The Set and Implant inspections are different types, so we need to check for both
  if (inspections && isSetInspections(inspections)) {
    if (inspections.CHECK_OUT?.status === 'CONFIRMED') return 'CHECKED_OUT';
    if (inspections.CHECK_OUT?.status === 'UNCONFIRMED') return 'CHECKED_IN';
    if (inspections.CHECK_IN?.status === 'CONFIRMED') return 'CHECKED_IN';
    if (inspections.CHECK_IN?.status === 'UNCONFIRMED') return 'RECEIVED';
  } else if (inspections && isImplantInspections(inspections)) {
    if (inspections.CHECK_OUT?.status === 'CONFIRMED') return 'CHECKED_OUT';
    if (inspections.CHECK_OUT?.status === 'UNCONFIRMED') return 'CHECKED_IN';
    if (inspections.CHECK_IN?.status === 'CONFIRMED') return 'CHECKED_IN';
    if (inspections.CHECK_IN?.status === 'UNCONFIRMED') return 'RECEIVED';
  } else {
    if (implantInspection?.CHECK_IN && !implantInspection?.CHECK_OUT) return 'CHECKED_IN';
    if (implantInspection?.CHECK_OUT) return 'CHECKED_OUT';
  }

  return SET_BUTTON_THEME_STATUS_MAPPING[status];
};

export const SET_BUTTON_ICON_MAPPING: Record<
  Exclude<ThemeStatus, 'PARTIALLY_RECEIVED' | 'PARTIALLY_DELIVERED'>,
  string
> = {
  // these are irrelevant and here for future proofing
  ORDERED: 'O',
  PARTIALLY_DISPATCHED: 'O',

  // relevant ones
  DISPATCHED: 'Di',
  DELIVERED: 'De',
  RECEIVED: 'R',
  PARTIALLY_CHECKED_IN: 'R',
  CHECKED_IN: 'Ci',
  PARTIALLY_CHECKED_OUT: 'Ci',
  CHECKED_OUT: 'Co',
  PARTIALLY_RETURNED: 'R',
  RETURNED: 'Rt',
  CANCELED: 'Ca',
  DELETED: 'D',
  NOT_DELIVERED: 'ND',
};

type SetButtonProps = ComponentProps<typeof Button> & {
  status: Status;
  inspections: Set['inspections'] | Implant['inspection'];
  implantInspection?: DeliveryImplantInspection;
  isPendingTurnaround?: boolean;
  showIcon?: boolean;
  selected?: boolean;
};

export const SetButton = ({
  status,
  inspections,
  implantInspection,
  isPendingTurnaround,
  showIcon,
  selected,
  sx,
  children,
  ...props
}: SetButtonProps) => {
  const themeStatus = isPendingTurnaround ? 'RECEIVED' : getThemeStatus({ status, inspections, implantInspection });
  const statusColors = theme.colors.statuses[themeStatus];
  const icon = SET_BUTTON_ICON_MAPPING[themeStatus];
  const borderWidth = selected ? 2 : 1;

  return (
    <Button
      role="button"
      sx={{
        minHeight: 48,
        borderRadius: 1,
        overflow: 'hidden',
        width: '100%',
        height: '52px',
        padding: 0,
        display: 'flex',
        border: `${borderWidth}px solid ${statusColors?.foreground}`,
        backgroundColor: selected ? statusColors?.muted : statusColors?.background,
        '&:hover': {
          backgroundColor: statusColors?.muted,
        },
        transition: 'background-color 0.2s ease-in-out',
        ...sx,
      }}
      {...props}
    >
      {showIcon && (
        <span
          style={{
            height: '100%',
            width: 32,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: statusColors?.foreground,
            fontSize: 14,
            fontWeight: 500,
            color: theme.colors.white,
          }}
        >
          {icon}
        </span>
      )}

      <span
        style={{
          flex: 1,
          flexDirection: 'column',
          overflow: 'hidden',
          padding: theme.layout.spacing.small,
          display: 'flex',
          fontSize: 14,
          color: theme.colors.black,
        }}
      >
        {children}
      </span>
    </Button>
  );
};
